import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DatePickerDMY from '../components/DatePickerDMY';
import RecurrenceEditor from '../components/calendar/RecurrenceEditor';
import ReminderEditor from '../components/calendar/ReminderEditor';
import ScopePrompt from '../components/calendar/ScopePrompt';
import CategoryManager, { CategoryIcon } from '../components/calendar/CategoryManager';
import {
  CaretLeft, CaretRight, Plus, ListChecks, Buildings, FunnelSimple, X, DotsThreeVertical,
  PencilSimple, Copy, CheckCircle, XCircle, Trash, Warning, Clock, Palette, ArrowsClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------------------- Date helpers --------------------
const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const parseYmd = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const fmtDmy = (s) => {
  const d = parseYmd(s);
  return d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : s;
};
const monthNamesEl = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος','Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];
const monthNamesEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const weekdaysEl = ['Δευ','Τρι','Τετ','Πεμ','Παρ','Σαβ','Κυρ'];
const weekdaysEn = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// Status meta
const STATUS_META = {
  pending:     { label_el: 'Εκκρεμεί',    label_en: 'Pending',     color: '#64748b' },
  in_progress: { label_el: 'Σε εξέλιξη',   label_en: 'In Progress', color: '#3b82f6' },
  completed:   { label_el: 'Ολοκληρώθηκε', label_en: 'Completed',   color: '#10b981' },
  cancelled:   { label_el: 'Ακυρώθηκε',    label_en: 'Cancelled',   color: '#a1a1aa' },
  overdue:     { label_el: 'Ληξιπρόθεσμο', label_en: 'Overdue',     color: '#ef4444' },
};

// Views
const VIEW_LABELS = {
  day:    { el: 'Ημέρα',    en: 'Day' },
  week:   { el: 'Εβδομάδα', en: 'Week' },
  month:  { el: 'Μήνας',    en: 'Month' },
  year:   { el: 'Έτος',     en: 'Year' },
  agenda: { el: 'Λίστα',    en: 'Agenda' },
};

// -------------------- Main --------------------
export default function EnhancedCalendarTasksPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEl = language === 'el';
  const monthNames = isEl ? monthNamesEl : monthNamesEn;
  const weekdays = isEl ? weekdaysEl : weekdaysEn;

  const [cursor, setCursor] = useState(() => {
    const t0 = new Date();
    return new Date(t0.getFullYear(), t0.getMonth(), 1);
  });
  const [view, setView] = useState('month');
  const [tasks, setTasks] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [categories, setCategories] = useState([]);

  // Filters
  const [filterBuilding, setFilterBuilding] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Phase 943 — Default hide completed/cancelled tasks so the calendar
  // stays clean. Users can toggle this back on to review history.
  const [showCompleted, setShowCompleted] = useState(false);

  // Dialogs
  const [taskDialog, setTaskDialog] = useState(null);      // {mode:'create'|'edit', task:{...}, occurrence_date?}
  const [categoryMgr, setCategoryMgr] = useState(false);
  const [scopePrompt, setScopePrompt] = useState(null);    // {kind, payload, onDone}
  const [buildingColorDlg, setBuildingColorDlg] = useState(false);

  // Quick actions menu (event popover)
  const [qaMenu, setQaMenu] = useState(null); // {x, y, task}

  // Drag & drop refs
  const dragTaskRef = useRef(null);

  const canEdit = ['admin', 'manager'].includes((user?.role || '').toLowerCase());

  // -------- Range for query based on view --------
  const range = useMemo(() => {
    if (view === 'day') {
      const d = new Date(cursor);
      return { start: ymd(d), end: ymd(d) };
    }
    if (view === 'week') {
      const d = new Date(cursor);
      const wd = (d.getDay() + 6) % 7;
      const s = new Date(d); s.setDate(d.getDate() - wd);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { start: ymd(s), end: ymd(e) };
    }
    if (view === 'year') {
      const y = cursor.getFullYear();
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    if (view === 'agenda') {
      const d = new Date(cursor);
      const s = new Date(d.getFullYear(), d.getMonth(), 1);
      const e = new Date(d.getFullYear(), d.getMonth() + 3, 0);
      return { start: ymd(s), end: ymd(e) };
    }
    // month: 6-week grid
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const wd = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - wd);
    const gridEnd = new Date(gridStart);
    gridEnd.setDate(gridStart.getDate() + 41);
    return { start: ymd(gridStart), end: ymd(gridEnd), gridStart };
  }, [cursor, view]);

  // -------- Fetchers --------
  const fetchTasks = useCallback(async () => {
    try {
      const params = { start: range.start, end: range.end };
      if (filterBuilding) params.building_id = filterBuilding;
      if (filterCategory) params.category_id = filterCategory;
      if (filterAssignee) params.assignee = filterAssignee;
      if (filterStatus && filterStatus !== 'overdue') params.status = filterStatus;
      const r = await axios.get(`${API}/calendar/tasks`, { params });
      let list = r.data.tasks || [];
      if (filterStatus === 'overdue') list = list.filter(t => t.status === 'overdue');
      if (!showCompleted) list = list.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
      setTasks(list);
    } catch (e) {
      toast.error(isEl ? 'Αποτυχία φόρτωσης' : 'Load failed');
    }
  }, [range.start, range.end, filterBuilding, filterCategory, filterAssignee, filterStatus, showCompleted, isEl]);

  const fetchBuildings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/buildings`);
      setBuildings(r.data || []);
    } catch (_) { /* ignore */ }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/task-categories`);
      setCategories(r.data || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { fetchBuildings(); fetchCategories(); }, [fetchBuildings, fetchCategories]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // -------- Group tasks by date --------
  const tasksByDate = useMemo(() => {
    const m = {};
    tasks.forEach(t => { (m[t.occurrence_date] ||= []).push(t); });
    return m;
  }, [tasks]);

  const distinctAssignees = useMemo(() => {
    const s = new Set();
    tasks.forEach(t => t.assignee && s.add(t.assignee));
    return Array.from(s).sort();
  }, [tasks]);

  const clearFilters = () => {
    setFilterBuilding(''); setFilterCategory(''); setFilterAssignee(''); setFilterStatus('');
  };
  const anyFilter = filterBuilding || filterCategory || filterAssignee || filterStatus || !showCompleted;

  // -------- Task dialog helpers --------
  const openCreateTask = (dateStr) => {
    if (!canEdit) { toast.error(isEl ? 'Μόνο Admin/Manager' : 'Admin/Manager only'); return; }
    setTaskDialog({
      mode: 'create',
      task: {
        building_id: filterBuilding || (buildings[0]?.building_id || ''),
        title: '',
        notes: '',
        start_date: dateStr || ymd(new Date()),
        end_date: '',
        time: '',
        assignee: '',
        category_id: filterCategory || '',
        is_important: false,
        status: 'pending',
        recurrence: null,
        reminders: [],
      },
    });
  };

  const openEditTask = async (t) => {
    if (!canEdit) { toast.error(isEl ? 'Μόνο Admin/Manager' : 'Admin/Manager only'); return; }
    try {
      const r = await axios.get(`${API}/tasks/${t.task_id}`);
      setTaskDialog({
        mode: 'edit',
        task: { ...r.data, reminders: r.data.reminders || [] },
        occurrence_date: t.occurrence_date,
        is_recurring: !!r.data.recurrence,
        is_multi_day: !!(r.data.end_date && r.data.end_date !== r.data.start_date),
      });
    } catch {
      toast.error(isEl ? 'Δεν βρέθηκε' : 'Not found');
    }
  };

  const saveTask = async (scope) => {
    const td = taskDialog;
    const t0 = td.task;
    if (!t0.building_id) { toast.error(isEl ? 'Επιλέξτε πολυκατοικία' : 'Pick a building'); return; }
    if (!t0.start_date) { toast.error(isEl ? 'Επιλέξτε ημερομηνία' : 'Pick a date'); return; }
    if (!(t0.title || '').trim()) { toast.error(isEl ? 'Απαιτείται τίτλος' : 'Title required'); return; }
    if (t0.end_date && t0.end_date < t0.start_date) {
      toast.error(isEl ? 'Λάθος εύρος ημερομηνιών' : 'Invalid date range'); return;
    }
    const payload = {
      building_id: t0.building_id, type: 'building',
      title: t0.title || '', notes: t0.notes || '',
      start_date: t0.start_date,
      end_date: t0.end_date || null,
      time: t0.time || null,
      assignee: t0.assignee || '',
      category_id: t0.category_id || null,
      is_important: !!t0.is_important,
      status: t0.status || 'pending',
      recurrence: t0.recurrence || null,
      reminders: (t0.reminders || []).map(r => ({
        offset_minutes: r.offset_minutes,
        channels: r.channels,
        reminder_id: r.reminder_id,
      })),
    };
    try {
      if (td.mode === 'create') {
        await axios.post(`${API}/tasks`, payload);
        toast.success(isEl ? 'Δημιουργήθηκε' : 'Created');
      } else if (scope && scope !== 'all' && (td.is_recurring || td.is_multi_day)) {
        // Scope edit via /calendar/tasks/{id}/scope
        await axios.put(`${API}/calendar/tasks/${t0.task_id}/scope`, payload, {
          params: { scope, occurrence_date: td.occurrence_date },
        });
        toast.success(isEl ? 'Αποθηκεύτηκε' : 'Saved');
      } else {
        await axios.put(`${API}/tasks/${t0.task_id}`, payload);
        toast.success(isEl ? 'Αποθηκεύτηκε' : 'Saved');
      }
      setTaskDialog(null);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const attemptSave = () => {
    const td = taskDialog;
    if (td.mode === 'edit' && (td.is_recurring || td.is_multi_day)) {
      setScopePrompt({
        kind: 'edit',
        title: isEl ? 'Αποθήκευση αλλαγών' : 'Save changes',
        description: isEl
          ? 'Πώς να εφαρμοστούν οι αλλαγές σε αυτή τη επαναλαμβανόμενη εργασία;'
          : 'How should these changes apply to the recurring task?',
        onChoose: (scope) => { setScopePrompt(null); saveTask(scope); },
      });
    } else {
      saveTask('all');
    }
  };

  // -------- Delete (with scope) --------
  const deleteTask = async (task, scope) => {
    try {
      if (scope && scope !== 'all' && (task.is_recurring || task.is_multi_day)) {
        await axios.delete(`${API}/calendar/tasks/${task.task_id}/scope`, {
          params: { scope, occurrence_date: task.occurrence_date },
        });
      } else {
        await axios.delete(`${API}/calendar/tasks/${task.task_id}/scope`, {
          params: { scope: 'all' },
        });
      }
      toast.success(isEl ? 'Διαγράφηκε' : 'Deleted');
      setQaMenu(null); setTaskDialog(null);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const attemptDelete = (task) => {
    if (task.is_recurring || task.is_multi_day) {
      setScopePrompt({
        kind: 'delete',
        title: isEl ? 'Διαγραφή' : 'Delete',
        description: isEl
          ? 'Ποιες εμφανίσεις θα διαγραφούν;'
          : 'Which occurrences should be deleted?',
        onChoose: (scope) => { setScopePrompt(null); deleteTask(task, scope); },
      });
    } else {
      if (!window.confirm(isEl ? 'Διαγραφή εργασίας;' : 'Delete this task?')) return;
      deleteTask(task, 'all');
    }
  };

  // -------- Quick actions --------
  const changeStatus = async (task, newStatus) => {
    try {
      await axios.patch(`${API}/calendar/tasks/${task.task_id}/status`, { status: newStatus });
      toast.success(isEl ? 'Ενημερώθηκε' : 'Updated');
      setQaMenu(null);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const duplicateTask = async (task) => {
    try {
      await axios.post(`${API}/calendar/tasks/${task.task_id}/duplicate`, {});
      toast.success(isEl ? 'Αντιγράφηκε' : 'Duplicated');
      setQaMenu(null);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  const skipOccurrence = async (task) => {
    try {
      await axios.post(`${API}/calendar/tasks/${task.task_id}/skip`, {
        occurrence_date: task.occurrence_date,
      });
      toast.success(isEl ? 'Παραλείφθηκε' : 'Skipped');
      setQaMenu(null);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Skip failed');
    }
  };

  // -------- Drag & drop --------
  const onDragStart = (e, task) => {
    if (!canEdit) { e.preventDefault(); return; }
    dragTaskRef.current = task;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', task.task_id); } catch (_) { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDropDate = (e, newDate) => {
    e.preventDefault();
    const t = dragTaskRef.current;
    dragTaskRef.current = null;
    if (!t || t.occurrence_date === newDate) return;

    const doMove = async (scope) => {
      try {
        await axios.post(`${API}/calendar/tasks/${t.task_id}/move`, {
          occurrence_date: t.occurrence_date, new_date: newDate,
        }, { params: { scope } });
        toast.success(isEl ? 'Μεταφέρθηκε' : 'Moved');
        fetchTasks();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Move failed');
      }
    };

    if (t.is_recurring || t.is_multi_day) {
      setScopePrompt({
        kind: 'move',
        title: isEl ? 'Μεταφορά εργασίας' : 'Move task',
        description: isEl
          ? `Πώς να εφαρμοστεί η αλλαγή ημερομηνίας στην ${fmtDmy(newDate)};`
          : `How should moving to ${fmtDmy(newDate)} apply?`,
        onChoose: (scope) => { setScopePrompt(null); doMove(scope); },
      });
    } else {
      doMove('all');
    }
  };

  // -------- Building color dialog --------
  const [pickingBld, setPickingBld] = useState(null);
  const saveBuildingColor = async (bid, color) => {
    try {
      await axios.put(`${API}/calendar/buildings/${bid}/color`, { color });
      toast.success(isEl ? 'Χρώμα αποθηκεύτηκε' : 'Colour saved');
      await fetchBuildings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  // -------- Navigation --------
  const nav = (dir) => {
    if (view === 'day' || view === 'week') {
      const days = view === 'day' ? 1 : 7;
      const d = new Date(cursor);
      d.setDate(d.getDate() + dir * days);
      setCursor(d);
      return;
    }
    if (view === 'year') {
      setCursor(new Date(cursor.getFullYear() + dir, 0, 1));
      return;
    }
    // month/agenda
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1));
  };
  const onToday = () => {
    const t0 = new Date();
    setCursor(view === 'day' || view === 'week' ? t0 : new Date(t0.getFullYear(), t0.getMonth(), 1));
  };

  const headerTitle = useMemo(() => {
    if (view === 'day') return fmtDmy(ymd(cursor));
    if (view === 'week') {
      const wd = (cursor.getDay() + 6) % 7;
      const s = new Date(cursor); s.setDate(cursor.getDate() - wd);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return `${fmtDmy(ymd(s))} — ${fmtDmy(ymd(e))}`;
    }
    if (view === 'year') return `${cursor.getFullYear()}`;
    return `${monthNames[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }, [view, cursor, monthNames]);

  // -------- Render --------
  return (
    <Layout>
      <div className="p-6 space-y-4" onClick={() => setQaMenu(null)}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="etasks-header">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {isEl ? 'Ημερολόγιο Εργασιών' : 'Task Calendar'}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {isEl
                ? 'Επαναλαμβανόμενες εργασίες, υπενθυμίσεις, drag & drop, φίλτρα ανά κατηγορία & κτίριο'
                : 'Recurring tasks, reminders, drag & drop, category & building filters'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Phase 996 — Quick link to dedicated Tasks Management page. */}
            <button
              type="button"
              onClick={() => navigate('/calendar/tasks-list')}
              className="text-xs uppercase tracking-wider px-3 py-1.5 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1.5"
              data-testid="open-tasks-list"
              title={isEl ? 'Δείτε όλες τις εργασίες σε λίστα' : 'View all tasks in a list'}
            >
              <ListChecks size={13} />
              {isEl ? 'Διαχείριση Εργασιών' : 'Tasks Management'}
            </button>
            {/* View switcher */}
            <div className="flex border border-zinc-200" data-testid="view-switcher">
              {Object.keys(VIEW_LABELS).map(v => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider ${view === v ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid={`view-${v}`}
                >{VIEW_LABELS[v][isEl ? 'el' : 'en']}</button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={onToday} className="rounded-none" data-testid="etasks-today">
              {isEl ? 'Σήμερα' : 'Today'}
            </Button>
            <button onClick={() => nav(-1)} className="p-2 border border-zinc-200 hover:bg-zinc-50" data-testid="etasks-prev"><CaretLeft size={16} /></button>
            <div className="px-3 py-1.5 text-sm font-semibold min-w-[220px] text-center border border-zinc-200" data-testid="etasks-title">
              {headerTitle}
            </div>
            <button onClick={() => nav(1)} className="p-2 border border-zinc-200 hover:bg-zinc-50" data-testid="etasks-next"><CaretRight size={16} /></button>
            <Button
              variant="outline" size="sm" onClick={() => setCategoryMgr(true)} className="rounded-none"
              disabled={!canEdit}
              data-testid="etasks-categories-btn"
            >
              <Palette size={14} className="mr-1" /> {isEl ? 'Κατηγορίες' : 'Categories'}
            </Button>
            <Button
              variant="outline" size="sm" onClick={() => setBuildingColorDlg(true)} className="rounded-none"
              disabled={!canEdit}
              data-testid="etasks-bldcolors-btn"
            >
              <Buildings size={14} className="mr-1" /> {isEl ? 'Χρώματα Κτιρίων' : 'Building Colours'}
            </Button>
            <Button onClick={() => openCreateTask(ymd(new Date()))} className="bg-zinc-950 rounded-none" size="sm" data-testid="etasks-new-btn">
              <Plus size={14} className="mr-1" /> {isEl ? 'Νέα Εργασία' : 'New Task'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/calendar')} className="rounded-none">
              {isEl ? 'Κλασικό' : 'Classic'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-3 space-y-2" data-testid="etasks-filters">
          <div className="flex items-center gap-2 flex-wrap">
            <FunnelSimple size={14} className="text-zinc-500" />
            <select value={filterBuilding} onChange={(e) => setFilterBuilding(e.target.value)}
                    className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white min-w-[160px]"
                    data-testid="filter-building">
              <option value="">{isEl ? 'Όλα τα Κτίρια' : 'All Buildings'}</option>
              {buildings.map(b => (
                <option key={b.building_id} value={b.building_id}>{b.name}</option>
              ))}
            </select>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                    className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white min-w-[160px]"
                    data-testid="filter-category">
              <option value="">{isEl ? 'Όλες οι Κατηγορίες' : 'All Categories'}</option>
              {categories.filter(c => c.active !== false).map(c => (
                <option key={c.category_id} value={c.category_id}>{c.name}</option>
              ))}
            </select>
            <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}
                    className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white min-w-[140px]"
                    data-testid="filter-assignee">
              <option value="">{isEl ? 'Όλοι' : 'All Assignees'}</option>
              {distinctAssignees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white min-w-[140px]"
                    data-testid="filter-status">
              <option value="">{isEl ? 'Όλες οι Καταστάσεις' : 'All Statuses'}</option>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <option key={k} value={k}>{m[isEl ? 'label_el' : 'label_en']}</option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)}
                      data-testid="filter-show-completed" />
              {isEl ? 'Εμφάνιση Ολοκληρωμένων' : 'Show Completed'}
            </label>
            {anyFilter && (
              <button onClick={clearFilters} className="text-xs text-red-600 hover:underline ml-auto" data-testid="filter-clear">
                <X size={12} className="inline mr-1" />{isEl ? 'Καθαρισμός' : 'Clear'}
              </button>
            )}
          </div>
          <div className="text-xs text-zinc-400">
            {isEl ? 'Σύνολο εμφανίσεων' : 'Occurrences shown'}: <span className="font-mono font-semibold text-zinc-700">{tasks.length}</span>
          </div>
        </div>

        {/* Body */}
        {view === 'month' && (
          <MonthGrid
            days={buildMonthDays(range.gridStart, cursor)}
            weekdays={weekdays}
            tasksByDate={tasksByDate}
            onSelectDay={(day) => canEdit && openCreateTask(day.key)}
            onEventClick={openEditTask}
            onEventContextMenu={(e, task) => { e.preventDefault(); setQaMenu({ x: e.clientX, y: e.clientY, task }); }}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDropDate}
            canEdit={canEdit}
            isEl={isEl}
          />
        )}
        {view === 'week' && (
          <WeekGrid
            startDate={parseYmd(range.start)}
            tasksByDate={tasksByDate}
            onEventClick={openEditTask}
            onEventContextMenu={(e, task) => { e.preventDefault(); setQaMenu({ x: e.clientX, y: e.clientY, task }); }}
            onSelectDay={(day) => canEdit && openCreateTask(day.key)}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDropDate}
            weekdays={weekdays}
            isEl={isEl}
          />
        )}
        {view === 'day' && (
          <DayView
            date={cursor}
            tasksByDate={tasksByDate}
            onEventClick={openEditTask}
            onEventContextMenu={(e, task) => { e.preventDefault(); setQaMenu({ x: e.clientX, y: e.clientY, task }); }}
            isEl={isEl}
          />
        )}
        {view === 'year' && (
          <YearView cursor={cursor} tasksByDate={tasksByDate} onSelectDate={(d) => { setCursor(parseYmd(d)); setView('day'); }} isEl={isEl} />
        )}
        {view === 'agenda' && (
          <AgendaView tasks={tasks} onEventClick={openEditTask} isEl={isEl} />
        )}
      </div>

      {/* Quick actions popover */}
      {qaMenu && (
        <div
          className="fixed z-50 bg-white border border-zinc-300 shadow-lg py-1 min-w-[200px]"
          style={{ left: qaMenu.x, top: qaMenu.y }}
          onClick={(e) => e.stopPropagation()}
          data-testid="qa-menu"
        >
          <QAItem onClick={() => { openEditTask(qaMenu.task); setQaMenu(null); }} Icon={PencilSimple} testid="qa-edit">
            {isEl ? 'Επεξεργασία' : 'Edit'}
          </QAItem>
          <QAItem onClick={() => duplicateTask(qaMenu.task)} Icon={Copy} testid="qa-duplicate" disabled={!canEdit}>
            {isEl ? 'Αντιγραφή' : 'Duplicate'}
          </QAItem>
          <QAItem onClick={() => changeStatus(qaMenu.task, 'completed')} Icon={CheckCircle} testid="qa-complete" disabled={!canEdit}>
            {isEl ? 'Ολοκλήρωση' : 'Complete'}
          </QAItem>
          <QAItem onClick={() => changeStatus(qaMenu.task, 'in_progress')} Icon={Clock} testid="qa-in-progress" disabled={!canEdit}>
            {isEl ? 'Σε Εξέλιξη' : 'In Progress'}
          </QAItem>
          <QAItem onClick={() => changeStatus(qaMenu.task, 'cancelled')} Icon={XCircle} testid="qa-cancel" disabled={!canEdit}>
            {isEl ? 'Ακύρωση' : 'Cancel'}
          </QAItem>
          {qaMenu.task.is_recurring && (
            <QAItem onClick={() => skipOccurrence(qaMenu.task)} Icon={ArrowsClockwise} testid="qa-skip" disabled={!canEdit}>
              {isEl ? 'Παράλειψη μιας εμφάνισης' : 'Skip this occurrence'}
            </QAItem>
          )}
          <div className="border-t border-zinc-200 my-1" />
          <QAItem onClick={() => { attemptDelete(qaMenu.task); setQaMenu(null); }} Icon={Trash} testid="qa-delete" danger disabled={!canEdit}>
            {isEl ? 'Διαγραφή' : 'Delete'}
          </QAItem>
        </div>
      )}

      {/* Task Dialog */}
      {taskDialog && (
        <TaskDialog
          taskDialog={taskDialog}
          setTaskDialog={setTaskDialog}
          buildings={buildings}
          categories={categories}
          onSave={attemptSave}
          onDelete={() => attemptDelete({ ...taskDialog.task, is_recurring: taskDialog.is_recurring, is_multi_day: taskDialog.is_multi_day, occurrence_date: taskDialog.occurrence_date })}
          canEdit={canEdit}
          isEl={isEl}
          language={language}
        />
      )}

      {/* Scope prompt */}
      {scopePrompt && (
        <ScopePrompt
          open={true}
          title={scopePrompt.title}
          description={scopePrompt.description}
          language={language}
          onCancel={() => setScopePrompt(null)}
          onChoose={scopePrompt.onChoose}
        />
      )}

      {/* Category manager */}
      <CategoryManager
        open={categoryMgr}
        onClose={() => setCategoryMgr(false)}
        onChanged={fetchCategories}
        language={language}
      />

      {/* Building colours */}
      <Dialog open={buildingColorDlg} onOpenChange={(v) => !v && setBuildingColorDlg(false)}>
        <DialogContent className="sm:max-w-[520px] rounded-none max-h-[80vh] overflow-y-auto" data-testid="bld-color-dlg">
          <DialogHeader>
            <DialogTitle>{isEl ? 'Χρώματα Κτιρίων' : 'Building Colours'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {buildings.map(b => (
              <div key={b.building_id} className="flex items-center gap-2 border border-zinc-200 px-2 py-1.5" data-testid={`bldcolor-row-${b.building_id}`}>
                <div className="w-5 h-5" style={{ backgroundColor: b.color || '#e4e4e7' }} />
                <span className="flex-1 text-sm">{b.name}</span>
                <Input type="color" value={b.color || '#3b82f6'}
                        onChange={(e) => saveBuildingColor(b.building_id, e.target.value)}
                        className="w-14 h-8 p-0" />
                {b.color && (
                  <button className="text-xs text-red-500 hover:underline"
                          onClick={() => saveBuildingColor(b.building_id, '')}>
                    {isEl ? 'Καθαρισμός' : 'Clear'}
                  </button>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuildingColorDlg(false)} className="rounded-none">
              {isEl ? 'Κλείσιμο' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// -------------------- Sub components --------------------

function QAItem({ children, Icon, onClick, testid, disabled, danger }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 text-sm w-full text-left hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed ${danger ? 'text-red-600' : 'text-zinc-800'}`}
      data-testid={testid}
    >
      <Icon size={13} />
      {children}
    </button>
  );
}

function buildMonthDays(gridStart, cursor) {
  const arr = [];
  const today = ymd(new Date());
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const key = ymd(d);
    arr.push({
      date: d, key,
      inMonth: d.getMonth() === cursor.getMonth(),
      isToday: key === today,
    });
  }
  return arr;
}

function TaskChip({ t, onClick, onContext, onDragStart, draggable, isEl }) {
  const st = STATUS_META[t.status] || STATUS_META.pending;
  const dot = t.category_color || t.building_color || st.color;
  const strike = t.status === 'completed' || t.status === 'cancelled';
  return (
    <button
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onClick}
      onContextMenu={onContext}
      className={`w-full text-left px-1.5 py-0.5 text-[10px] truncate flex items-center gap-1 hover:opacity-90 ${strike ? 'line-through opacity-60' : ''}`}
      style={{ backgroundColor: dot, color: '#fff' }}
      title={`${t.title}${t.building_name ? ' · ' + t.building_name : ''}${t.status === 'overdue' ? ' ⚠' : ''}`}
      data-testid={`etask-chip-${t.occurrence_id}`}
    >
      {t.category_icon && <CategoryIcon name={t.category_icon} size={10} className="opacity-90 flex-shrink-0" />}
      {t.is_recurring && <ArrowsClockwise size={9} className="flex-shrink-0" />}
      {t.status === 'overdue' && <Warning size={10} className="flex-shrink-0" />}
      <span className="truncate">{t.time ? `${t.time} ` : ''}{t.title}</span>
    </button>
  );
}

function MonthGrid({ days, weekdays, tasksByDate, onSelectDay, onEventClick, onEventContextMenu, onDragStart, onDragOver, onDrop, canEdit, isEl }) {
  return (
    <div className="bg-white border border-zinc-200" data-testid="etasks-month-grid">
      <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
        {weekdays.map(d => (
          <div key={d} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 text-center border-r last:border-r-0 border-zinc-200">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const isLastRow = i >= 35;
          const list = tasksByDate[day.key] || [];
          return (
            <div
              key={day.key}
              className={`min-h-[110px] p-1.5 border-r border-zinc-200 ${isLastRow ? '' : 'border-b'} ${day.inMonth ? 'bg-white' : 'bg-zinc-50/50'} ${(i+1)%7===0 ? 'border-r-0' : ''}`}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, day.key)}
              data-testid={`etasks-day-${day.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-mono font-semibold ${day.isToday ? 'bg-zinc-900 text-white px-1.5 py-0.5' : day.inMonth ? 'text-zinc-900' : 'text-zinc-300'}`}>
                  {day.date.getDate()}
                </span>
                {day.inMonth && canEdit && (
                  <button onClick={() => onSelectDay(day)} className="text-zinc-300 hover:text-zinc-900" title={isEl ? 'Νέα Εργασία' : 'Add Task'} data-testid={`etasks-add-${day.key}`}>
                    <Plus size={12} />
                  </button>
                )}
              </div>
              <div className="space-y-0.5">
                {list.slice(0, 4).map(t => (
                  <TaskChip
                    key={t.occurrence_id}
                    t={t}
                    isEl={isEl}
                    draggable={canEdit}
                    onClick={() => onEventClick(t)}
                    onContext={(e) => onEventContextMenu(e, t)}
                    onDragStart={(e) => onDragStart(e, t)}
                  />
                ))}
                {list.length > 4 && (
                  <div className="text-[10px] text-zinc-500 pl-1" data-testid={`etasks-more-${day.key}`}>
                    +{list.length - 4} {isEl ? 'ακόμη' : 'more'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekGrid({ startDate, tasksByDate, onEventClick, onEventContextMenu, onSelectDay, onDragStart, onDragOver, onDrop, weekdays, isEl }) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startDate); d.setDate(startDate.getDate() + i);
    days.push({ date: d, key: ymd(d), isToday: ymd(d) === ymd(new Date()) });
  }
  return (
    <div className="bg-white border border-zinc-200" data-testid="etasks-week-grid">
      <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
        {days.map((d, i) => (
          <div key={d.key} className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-600 text-center border-r last:border-r-0 border-zinc-200">
            {weekdays[i]} <span className={`ml-1 font-mono ${d.isToday ? 'bg-zinc-900 text-white px-1' : ''}`}>{d.date.getDate()}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map(day => (
          <div key={day.key}
               onDragOver={onDragOver}
               onDrop={(e) => onDrop(e, day.key)}
               className="min-h-[420px] p-2 border-r last:border-r-0 border-zinc-200"
               data-testid={`etasks-wk-day-${day.key}`}>
            <div className="space-y-1">
              {(tasksByDate[day.key] || []).map(t => (
                <TaskChip key={t.occurrence_id} t={t} isEl={isEl} draggable
                  onClick={() => onEventClick(t)}
                  onContext={(e) => onEventContextMenu(e, t)}
                  onDragStart={(e) => onDragStart(e, t)} />
              ))}
              <button className="mt-2 text-[10px] text-zinc-400 hover:text-zinc-800" onClick={() => onSelectDay(day)} data-testid={`etasks-wk-add-${day.key}`}>
                + {isEl ? 'Προσθήκη' : 'Add'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DayView({ date, tasksByDate, onEventClick, onEventContextMenu, isEl }) {
  const key = ymd(date);
  const list = tasksByDate[key] || [];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const withHour = list.map(t => {
    const h = parseInt((t.time || '').slice(0, 2), 10);
    return { ...t, _hour: isNaN(h) ? -1 : h };
  });
  const untimed = withHour.filter(t => t._hour === -1);
  return (
    <div className="bg-white border border-zinc-200" data-testid="etasks-day-grid">
      {untimed.length > 0 && (
        <div className="border-b border-zinc-200 p-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">
            {isEl ? 'Ολοήμερες' : 'All Day'}
          </div>
          <div className="space-y-1">
            {untimed.map(t => (
              <TaskChip key={t.occurrence_id} t={t} isEl={isEl}
                onClick={() => onEventClick(t)}
                onContext={(e) => onEventContextMenu(e, t)}
                onDragStart={() => {}} draggable={false} />
            ))}
          </div>
        </div>
      )}
      <div className="divide-y divide-zinc-100">
        {hours.map(h => {
          const inH = withHour.filter(t => t._hour === h);
          return (
            <div key={h} className="flex" data-testid={`etasks-hour-${h}`}>
              <div className="w-16 text-[11px] text-zinc-400 font-mono px-2 py-2 border-r border-zinc-200 text-right">
                {String(h).padStart(2, '0')}:00
              </div>
              <div className="flex-1 min-h-[38px] p-1 space-y-1">
                {inH.map(t => (
                  <TaskChip key={t.occurrence_id} t={t} isEl={isEl}
                    onClick={() => onEventClick(t)}
                    onContext={(e) => onEventContextMenu(e, t)}
                    onDragStart={() => {}} draggable={false} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ cursor, tasksByDate, onSelectDate, isEl }) {
  const year = cursor.getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => i);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="etasks-year-grid">
      {months.map(m => {
        const first = new Date(year, m, 1);
        const daysInMonth = new Date(year, m + 1, 0).getDate();
        const startWd = (first.getDay() + 6) % 7;
        const cells = [];
        for (let i = 0; i < startWd; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
          const dt = new Date(year, m, d);
          const key = ymd(dt);
          cells.push({ d, key, count: (tasksByDate[key] || []).length, isToday: key === ymd(new Date()) });
        }
        return (
          <div key={m} className="bg-white border border-zinc-200 p-2" data-testid={`etasks-year-m-${m + 1}`}>
            <div className="text-xs font-semibold text-zinc-700 mb-1 uppercase tracking-wider">
              {(isEl ? monthNamesEl : monthNamesEn)[m]}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {cells.map((c, i) => (
                c ? (
                  <button
                    key={i} onClick={() => onSelectDate(c.key)}
                    className={`text-[10px] p-1 border ${c.isToday ? 'bg-zinc-900 text-white border-zinc-900' : c.count > 0 ? 'bg-emerald-50 border-emerald-200 text-zinc-900' : 'border-zinc-100 text-zinc-500'}`}
                    title={`${c.key} · ${c.count} task${c.count === 1 ? '' : 's'}`}
                    data-testid={`etasks-year-cell-${c.key}`}
                  >{c.d}</button>
                ) : <div key={i} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgendaView({ tasks, onEventClick, isEl }) {
  const sorted = [...tasks].sort((a, b) => (a.occurrence_date + (a.time || '')).localeCompare(b.occurrence_date + (b.time || '')));
  const groups = {};
  sorted.forEach(t => { (groups[t.occurrence_date] ||= []).push(t); });
  return (
    <div className="bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="etasks-agenda">
      {Object.keys(groups).length === 0 && (
        <div className="p-6 text-center text-sm text-zinc-400">
          {isEl ? 'Καμία εργασία στο διάστημα' : 'No tasks in range'}
        </div>
      )}
      {Object.entries(groups).map(([date, list]) => (
        <div key={date} className="p-3" data-testid={`etasks-agenda-day-${date}`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">{fmtDmy(date)}</div>
          <div className="space-y-1">
            {list.map(t => {
              const st = STATUS_META[t.status] || STATUS_META.pending;
              return (
                <button key={t.occurrence_id} onClick={() => onEventClick(t)}
                        className="w-full flex items-center gap-2 text-left border border-zinc-200 px-2 py-1.5 hover:bg-zinc-50"
                        data-testid={`etasks-agenda-row-${t.occurrence_id}`}>
                  <span className="w-2.5 h-2.5" style={{ backgroundColor: t.category_color || t.building_color || st.color }} />
                  {t.category_icon && <CategoryIcon name={t.category_icon} size={12} className="text-zinc-500" />}
                  <span className="text-sm flex-1 truncate">
                    {t.time ? <span className="font-mono text-zinc-500 mr-2">{t.time}</span> : null}
                    {t.title}
                  </span>
                  {t.building_name && <span className="text-[10px] text-zinc-500">{t.building_name}</span>}
                  <span className="text-[10px] px-1 border" style={{ borderColor: st.color, color: st.color }}>
                    {st[isEl ? 'label_el' : 'label_en']}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------------------- Task Dialog --------------------
function TaskDialog({ taskDialog, setTaskDialog, buildings, categories, onSave, onDelete, canEdit, isEl, language }) {
  const t0 = taskDialog.task;
  const setT = (patch) => setTaskDialog({ ...taskDialog, task: { ...t0, ...patch } });
  return (
    <Dialog open={true} onOpenChange={(v) => !v && setTaskDialog(null)}>
      <DialogContent className="sm:max-w-[640px] rounded-none max-h-[90vh] overflow-y-auto" data-testid="etasks-dialog">
        <DialogHeader>
          <DialogTitle>
            {taskDialog.mode === 'edit'
              ? (isEl ? 'Επεξεργασία Εργασίας' : 'Edit Task')
              : (isEl ? 'Νέα Εργασία' : 'New Task')}
            {(taskDialog.is_recurring || taskDialog.is_multi_day) && (
              <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-600">
                {taskDialog.is_recurring ? (isEl ? '· Επαναλαμβανόμενη' : '· Recurring') : (isEl ? '· Πολυήμερη' : '· Multi-day')}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{isEl ? 'Πολυκατοικία' : 'Building'}</Label>
              <select value={t0.building_id || ''} onChange={(e) => setT({ building_id: e.target.value })}
                      disabled={taskDialog.mode === 'edit'}
                      className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
                      data-testid="etasks-dlg-building">
                <option value="">—</option>
                {buildings.map(b => (<option key={b.building_id} value={b.building_id}>{b.name}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">{isEl ? 'Κατηγορία' : 'Category'}</Label>
              <select value={t0.category_id || ''} onChange={(e) => setT({ category_id: e.target.value || null })}
                      className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
                      data-testid="etasks-dlg-category">
                <option value="">—</option>
                {categories.filter(c => c.active !== false).map(c => (
                  <option key={c.category_id} value={c.category_id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">{isEl ? 'Τίτλος' : 'Title'}</Label>
            <Input value={t0.title || ''} onChange={(e) => setT({ title: e.target.value })}
                    className="mt-1 rounded-none" data-testid="etasks-dlg-title" />
          </div>
          <div>
            <Label className="text-xs">{isEl ? 'Περιγραφή' : 'Description'}</Label>
            <Textarea value={t0.notes || ''} onChange={(e) => setT({ notes: e.target.value })} rows={3}
                       className="mt-1 rounded-none text-sm" data-testid="etasks-dlg-notes" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{isEl ? 'Έναρξη' : 'Start Date'}</Label>
              <DatePickerDMY value={t0.start_date || ''} onChange={(v) => setT({ start_date: v })} data-testid="etasks-dlg-start" />
            </div>
            <div>
              <Label className="text-xs">{isEl ? 'Λήξη (Πολυήμερη)' : 'End Date (Multi-day)'}</Label>
              <DatePickerDMY value={t0.end_date || ''} onChange={(v) => setT({ end_date: v })} data-testid="etasks-dlg-end" />
            </div>
            <div>
              <Label className="text-xs">{isEl ? 'Ώρα' : 'Time'}</Label>
              <Input type="time" value={t0.time || ''} onChange={(e) => setT({ time: e.target.value })}
                      className="mt-1 rounded-none" data-testid="etasks-dlg-time" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{isEl ? 'Ανάθεση' : 'Assignee'}</Label>
              <Input value={t0.assignee || ''} onChange={(e) => setT({ assignee: e.target.value })}
                      placeholder={isEl ? 'π.χ. name@example.com' : 'e.g. name@example.com'}
                      className="mt-1 rounded-none" data-testid="etasks-dlg-assignee" />
              <div className="text-[10px] text-zinc-400 mt-1">
                {isEl ? 'Email → λαμβάνει email υπενθυμίσεις' : 'Use an email to receive email reminders'}
              </div>
            </div>
            <div>
              <Label className="text-xs">{isEl ? 'Κατάσταση' : 'Status'}</Label>
              <select value={t0.status || 'pending'} onChange={(e) => setT({ status: e.target.value })}
                      className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white"
                      data-testid="etasks-dlg-status">
                <option value="pending">{STATUS_META.pending[isEl ? 'label_el' : 'label_en']}</option>
                <option value="in_progress">{STATUS_META.in_progress[isEl ? 'label_el' : 'label_en']}</option>
                <option value="completed">{STATUS_META.completed[isEl ? 'label_el' : 'label_en']}</option>
                <option value="cancelled">{STATUS_META.cancelled[isEl ? 'label_el' : 'label_en']}</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!t0.is_important} onChange={(e) => setT({ is_important: e.target.checked })}
                    data-testid="etasks-dlg-important" />
            <span>{isEl ? 'Σημαντικό' : 'Important'}</span>
          </label>
          <RecurrenceEditor
            value={t0.recurrence}
            onChange={(v) => setT({ recurrence: v })}
            language={language}
          />
          <ReminderEditor
            value={t0.reminders || []}
            onChange={(v) => setT({ reminders: v })}
            language={language}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          {taskDialog.mode === 'edit' && canEdit && (
            <Button variant="outline" onClick={onDelete} className="rounded-none mr-auto text-red-600" data-testid="etasks-dlg-delete">
              {isEl ? 'Διαγραφή' : 'Delete'}
            </Button>
          )}
          <Button variant="outline" onClick={() => setTaskDialog(null)} className="rounded-none" data-testid="etasks-dlg-cancel">
            {isEl ? 'Άκυρο' : 'Cancel'}
          </Button>
          <Button onClick={onSave} disabled={!canEdit} className="rounded-none bg-zinc-900 text-white hover:bg-zinc-800" data-testid="etasks-dlg-save">
            {isEl ? 'Αποθήκευση' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
